import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Receipt,
  Email,
  Phone,
  Home,
  CalendarToday,
  AttachMoney,
  Campaign,
  Repeat
} from '@mui/icons-material';
import { donationService } from '../services/api';
import { BRAND, primaryButtonSx } from '../theme/pageStyles';

const DonationSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [donation, setDonation] = useState(null);
  const [error, setError] = useState(null);

  const status = searchParams.get('status'); // 'success' or 'failed'
  const failReason = searchParams.get('reason') || '';
  const donationId = searchParams.get('donationId');
  const transactionId = searchParams.get('transactionId');

  useEffect(() => {
    const fetchDonationDetails = async () => {
      if (!donationId) {
        setError('Donation ID not provided');
        setLoading(false);
        return;
      }

      try {
        // Try to fetch donation details from API
        if (donationService.getDonationById) {
          const response = await donationService.getDonationById(donationId);
          if (response?.data?.success && response?.data?.data) {
            setDonation(response.data.data);
          } else if (response?.data) {
            setDonation(response.data);
          } else {
            // Fallback to URL params
            setDonation({
              id: donationId,
              transaction_id: transactionId,
              status: status
            });
          }
        } else {
          // API method not available, use URL params
          setDonation({
            id: donationId,
            transaction_id: transactionId,
            status: status
          });
        }
      } catch (err) {
        console.error('Error fetching donation:', err);
        // Use URL params as fallback
        setDonation({
          id: donationId,
          transaction_id: transactionId,
          status: status
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDonationDetails();
  }, [donationId, transactionId, status]);

  const isSuccess = status === 'success';
  const isFailed = status === 'failed';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="md">
        {/* Status Icon Card */}
        <Card
          sx={{
            mb: 4,
            background: isSuccess
              ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
              : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            border: `2px solid ${isSuccess ? '#10b981' : '#ef4444'}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            textAlign: 'center',
            py: 6
          }}
        >
          <Box sx={{ mb: 3 }}>
            {isSuccess ? (
              <CheckCircle sx={{ fontSize: 100, color: '#10b981' }} />
            ) : (
              <Cancel sx={{ fontSize: 100, color: '#ef4444' }} />
            )}
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: isSuccess ? '#059669' : '#dc2626',
              mb: 2
            }}
          >
            {isSuccess ? 'Donation Successful!' : 'Donation Failed'}
          </Typography>
          <Typography variant="h6" sx={{ color: '#667085', fontWeight: 500 }}>
            {isSuccess
              ? 'Thank you for your generous contribution!'
              : 'We encountered an issue processing your donation. Please try again.'}
          </Typography>
        </Card>

        {isSuccess && donation && (
          <>
            {/* Donation Details Card */}
            <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 3 }}>
                  Donation Details
                </Typography>

                <Grid container spacing={3}>
                  {/* Amount */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: '#d1fae5',
                          borderRadius: '12px',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <AttachMoney sx={{ color: '#059669', fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          DONATION AMOUNT
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#059669' }}>
                          ₹{donation.amount?.toLocaleString() || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Transaction ID */}
                  {donation.transaction_id && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            backgroundColor: '#e0e7ff',
                            borderRadius: '12px',
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Receipt sx={{ color: '#6366f1', fontSize: 28 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                            TRANSACTION ID
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: '500', color: '#1a202c', fontFamily: 'monospace' }}
                          >
                            {donation.transaction_id}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Donation ID */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: '#fef3c7',
                          borderRadius: '12px',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Receipt sx={{ color: '#d97706', fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          DONATION ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: '500', color: '#1a202c' }}>
                          #{donation.id || donationId}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Date */}
                  {donation.created_at && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            backgroundColor: '#f3e8ff',
                            borderRadius: '12px',
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <CalendarToday sx={{ color: '#9333ea', fontSize: 28 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                            DATE
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: '500', color: '#1a202c' }}>
                            {new Date(donation.created_at).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Campaign Info */}
                {donation.campaign_title && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Campaign sx={{ color: BRAND.green, fontSize: 24 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#1a202c' }}>
                        Campaign
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#667085', ml: 5 }}>
                      {donation.campaign_title}
                    </Typography>
                  </Box>
                )}

                {/* Recurring Info */}
                {(donation.is_recurring === true || donation.is_recurring === 1) && (
                  <Box
                    sx={{
                      backgroundColor: '#f5f3ff',
                      border: '1px solid #c4b5fd',
                      borderRadius: 2,
                      p: 2,
                      mb: 3
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Repeat sx={{ color: '#7c3aed' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: '600', color: '#7c3aed' }}>
                        Recurring Donation
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#667085', ml: 4 }}>
                      Frequency: {donation.recurring_frequency
                        ? donation.recurring_frequency.charAt(0).toUpperCase() +
                          donation.recurring_frequency.slice(1)
                        : 'N/A'}
                      {donation.recurring_end_date && (
                        <> • Ends: {new Date(donation.recurring_end_date).toLocaleDateString()}</>
                      )}
                    </Typography>
                  </Box>
                )}

                {/* Payment Method */}
                {donation.payment_method && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      PAYMENT METHOD
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={donation.payment_method.toUpperCase()}
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Donor Information Card */}
            {donation.name && (
              <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 3 }}>
                    Donor Information
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                        NAME
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: '500', color: '#1a202c', mt: 0.5 }}>
                        {donation.name}
                      </Typography>
                    </Grid>

                    {donation.email && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Email sx={{ fontSize: 18, color: '#667085' }} />
                          <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                            EMAIL
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: '500', color: '#1a202c' }}>
                          {donation.email}
                        </Typography>
                      </Grid>
                    )}

                    {donation.phone && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Phone sx={{ fontSize: 18, color: '#667085' }} />
                          <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                            PHONE
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: '500', color: '#1a202c' }}>
                          {donation.phone}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* 80G Receipt Info */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: '#f0fdf4',
                border: '1px solid #86efac',
                '& .MuiAlert-icon': { color: '#15803d' }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: '600', color: '#15803d', mb: 0.5 }}>
                80G Tax Receipt
              </Typography>
              <Typography variant="body2" sx={{ color: '#166534' }}>
                Your 80G tax receipt has been sent to your email address. You can use this receipt
                to claim tax deductions under Section 80G of the Income Tax Act.
              </Typography>
            </Alert>
          </>
        )}

        {isFailed && (
          <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 4 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 1 }}>
                  Payment Processing Failed
                </Typography>
                {failReason ? (
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {decodeURIComponent(failReason)}
                  </Typography>
                ) : null}
                {failReason.toLowerCase().includes('international') ? (
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    This Razorpay account accepts <strong>Indian cards, UPI, netbanking, and wallets</strong> only.
                    International cards are not enabled on the gateway yet.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    We were unable to process your donation. This could be due to insufficient funds,
                    bank restrictions, network issues, or payment gateway limits.
                  </Typography>
                )}
                {process.env.NODE_ENV === 'development' && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                      Sandbox test options (India):
                    </Typography>
                    <Typography variant="caption" component="div">
                      Card: 4111 1111 1111 1111 · UPI: success@razorpay · Any future expiry & CVV
                    </Typography>
                  </Box>
                )}
              </Alert>

              {donationId && (
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9fafb', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    DONATION ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: '500', color: '#1a202c', fontFamily: 'monospace' }}>
                    #{donationId}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {isSuccess ? (
            <>
              <Button
                variant="contained"
                size="large"
                startIcon={<Home />}
                onClick={() => navigate('/')}
                sx={{ ...primaryButtonSx, px: 4 }}
              >
                Back to Home
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Campaign />}
                onClick={() => navigate('/campaigns')}
                sx={{
                  borderColor: BRAND.green,
                  color: BRAND.green,
                  px: 4,
                  py: 1.5,
                  height: 50,
                  borderRadius: 28,
                  '&:hover': { borderColor: BRAND.greenHover, backgroundColor: '#f0fdf4' }
                }}
              >
                View Campaigns
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(-1)}
                sx={{ ...primaryButtonSx, px: 4 }}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Home />}
                onClick={() => navigate('/')}
                sx={{
                  borderColor: BRAND.green,
                  color: BRAND.green,
                  px: 4,
                  py: 1.5,
                  height: 50,
                  borderRadius: 28
                }}
              >
                Back to Home
              </Button>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default DonationSuccessPage;

